import Loader from '@/components/Loader'
import { CONCIERGE_TYPES, ConciergeType } from '@/store/constant'
import { Concierge, parseConcierges } from '@/types/concierge'
import { useAuth } from '@hooks/useAuth'
import {
  collection,
  getDocs,
  getFirestore,
  query,
  where,
} from 'firebase/firestore'
import { ReactNode, useEffect, useRef, useState } from 'react'
import { ConciergeListContext } from './ConciergeListContext'

const CONCIERGES_COLLECTION = 'concierges'
const SELECTED_CONCIERGE_ID = 'SELECTED_CONCIERGE_ID'

interface ConciergeListProviderProps {
  children: ReactNode
}

export function ConciergeListProvider({
  children,
}: ConciergeListProviderProps) {
  const [selectedConcierge, setSelectedConcierge] = useState<Concierge | null>(
    null,
  )
  const [conciergesList, setConciergesList] = useState<Concierge[]>([])
  const [conciergeType, setConciergeType] = useState<ConciergeType>(
    CONCIERGE_TYPES.WEB_WISDOM,
  )
  const hasFetchedRef = useRef(false)
  const { user, isAdmin } = useAuth()
  const db = getFirestore()

  useEffect(() => {
    if (hasFetchedRef.current) return
    async function fetchConcierges() {
      if (hasFetchedRef.current) return

      let q

      if (isAdmin) {
        q = query(collection(db, CONCIERGES_COLLECTION))
      } else {
        q = query(
          collection(db, CONCIERGES_COLLECTION),
          where('email', '==', user.email),
        )
      }

      let snapshot
      try {
        snapshot = await getDocs(q)
      } catch (error) {
        console.error('Error fetching concierges:', error)
        console.error('Error fetching concierges:', q)
        hasFetchedRef.current = true
        return
      }

      if (snapshot.empty) {
        console.warn(`Empty concierge snapshot for ${user.email}`)
        setSelectedConcierge(null)
        setConciergesList([])
        hasFetchedRef.current = true
        return
      }

      const docsFound = snapshot.docs.length
      console.log('Concierges found', docsFound)

      const fetchedConcierges = parseConcierges(snapshot)

      setConciergesList(fetchedConcierges)

      const params = new URLSearchParams(window.location.search)
      const queryParamConciergeId = params.get('conciergeId')

      let selectedConcierge = fetchedConcierges[0]

      const savedConciergeId = localStorage.getItem(SELECTED_CONCIERGE_ID)
      const savedConcierge = fetchedConcierges.find(
        (c) => c.id === savedConciergeId,
      )
      if (savedConcierge) {
        selectedConcierge = savedConcierge
      }

      const queryConcierge = fetchedConcierges.find(
        (c) => c.id === queryParamConciergeId,
      )
      if (queryConcierge) {
        selectedConcierge = queryConcierge
      }

      setSelectedConcierge(selectedConcierge)
      hasFetchedRef.current = true
    }

    fetchConcierges()
  }, [user.email, isAdmin, db])

  function handleSetSelectedConcierge(newConcierge: Concierge) {
    setSelectedConcierge(newConcierge)
    localStorage.setItem(SELECTED_CONCIERGE_ID, newConcierge.id)
  }

  if (!hasFetchedRef.current) {
    return <Loader />
  }

  return (
    <ConciergeListContext.Provider
      value={{
        conciergesList,
        selectedConcierge,
        setSelectedConcierge: handleSetSelectedConcierge,
        conciergeType,
        setConciergeType,
      }}
    >
      {children}
    </ConciergeListContext.Provider>
  )
}
